import type { LoginResponse, LoginValidationErrors } from '@ts-auth/interfaces'
import type { AxiosError } from 'axios'

import { useAuthStore } from '@stores/auth'

import { useAdminRoutes } from '@composables/routes/admin-route.hook'

interface LoginPayload {
  email: string | null
  password: string | null
}

interface UseAuthApiOptions {
  onStart?: () => void
  onSuccess?: (data: LoginResponse) => void
  onError?: (err: AxiosError<LoginValidationErrors>) => void
}

interface UseLogoutApiOptions {
  onStart?: () => void
  onSuccess?: () => void
  onError?: (err: AxiosError) => void
}

interface UseAuthApiReturn {
  login: (payload: LoginPayload, options?: UseAuthApiOptions) => Promise<LoginResponse | null>
  logout: (options?: UseLogoutApiOptions) => Promise<void>
}

export const useAuthApi = (): UseAuthApiReturn => {
  const { $axios } = useNuxtApp()
  const authStore = useAuthStore()

  const login = async (payload: LoginPayload, options?: UseAuthApiOptions): Promise<LoginResponse | null> => {
    authStore.setLoading(true)
    options?.onStart?.()

    const url = useAdminRoutes('admin.auth.login')
    if (!url) return null

    try {
      const { data } = await $axios.post<LoginResponse>(url, payload)
      authStore.setLoading(false)
      options?.onSuccess?.(data)
      return data
    } catch (err) {
      authStore.setLoading(false)
      options?.onError?.(err as AxiosError<LoginValidationErrors>)
      return null
    }
  }

  const logout = async (options?: UseLogoutApiOptions): Promise<void> => {
    authStore.setLoading(true)
    options?.onStart?.()

    const url = useAdminRoutes('admin.auth.logout')
    if (!url) return

    try {
      await $axios.post(url)
      authStore.setLoading(false)
      authStore._clearTokens()
      options?.onSuccess?.()
    } catch (err) {
      authStore.setLoading(false)
      options?.onError?.(err as AxiosError)
    }
  }

  return {
    login,
    logout,
  }
}
